import { jsx } from 'react/jsx-runtime';
import lottie from 'lottie-web';
import { useRef, useState, useEffect } from 'react';
import { delayRender, useCurrentFrame, continueRender } from 'remotion';

/**
 * @description Get the basic metadata such as dimensions, duration and framerate of a Lottie animation.
 * @see [Documentation](https://www.remotion.dev/docs/lottie/getlottiemetadata)
 */
const getLottieMetadata = (animationData) => {
    const width = animationData.w;
    const height = animationData.h;
    const framerate = animationData.fr;
    const durationInFrames = animationData.op;
    if (![width, height, framerate, durationInFrames].every(Boolean)) {
        return null;
    }
    return {
        durationInFrames: Math.floor(durationInFrames),
        durationInSeconds: durationInFrames / framerate,
        fps: framerate,
        height,
        width,
    };
};

const getNextFrame = ({ currentFrame, direction, loop, totalFrames, }) => {
    const nextFrame = loop
        ? currentFrame % totalFrames
        : Math.min(currentFrame, totalFrames);
    if (direction === 'backward') {
        return totalFrames - nextFrame;
    }
    return nextFrame;
};

const validateLoop = (loop) => {
    if (typeof loop === 'undefined') {
        return;
    }
    if (typeof loop !== 'boolean') {
        throw new TypeError(`The "loop" prop must be a boolean or undefined, but is "${JSON.stringify(loop)}"`);
    }
};

const validatePlaybackRate = (playbackRate) => {
    if (typeof playbackRate === 'undefined') {
        return;
    }
    if (typeof playbackRate !== 'number') {
        throw new TypeError(`The "playbackRate" prop must be a number or undefined, but is ${JSON.stringify(playbackRate)}`);
    }
    if (Number.isNaN(playbackRate) || !Number.isFinite(playbackRate)) {
        throw new TypeError(`The "playbackRate" props must be a real number, but is ${playbackRate}`);
    }
    if (playbackRate <= 0) {
        throw new TypeError(`The "playbackRate" props must be positive, but is ${playbackRate}`);
    }
};

/**
 * @description	Part of the @remotion/lottie package.
 * @see [Documentation](https://www.remotion.dev/docs/lottie/lottie)
 */
const Lottie = ({ animationData, className, direction, loop, playbackRate, style, onAnimationLoaded, }) => {
    if (typeof animationData !== 'object') {
        throw new Error('animationData should be provided as an object. If you only have the path to the JSON file, load it and pass it as animationData. See https://remotion.dev/docs/lottie/lottie#example for more information.');
    }
    validatePlaybackRate(playbackRate);
    validateLoop(loop);
    const animationRef = useRef();
    const currentFrameRef = useRef(null);
    const containerRef = useRef(null);
    const onAnimationLoadedRef = useRef();
    onAnimationLoadedRef.current = onAnimationLoaded;
    const [handle] = useState(() => delayRender('Waiting for Lottie animation to load'));
    const frame = useCurrentFrame();
    currentFrameRef.current = frame;
    useEffect(() => {
        var _a;
        if (!containerRef.current) {
            return;
        }
        animationRef.current = lottie.loadAnimation({
            container: containerRef.current,
            autoplay: false,
            animationData,
        });
        const { current: animation } = animationRef;
        const onComplete = () => {
            var _a, _b;
            // Seek frame twice to avoid Lottie initialization bug:
            // See LottieInitializationBugfix composition in the example project for a repro.
            // We can work around it by seeking twice, initially.
            if (currentFrameRef.current) {
                (_a = animationRef.current) === null || _a === void 0 ? void 0 : _a.goToAndStop(Math.max(0, currentFrameRef.current - 1), true);
                (_b = animationRef.current) === null || _b === void 0 ? void 0 : _b.goToAndStop(currentFrameRef.current, true);
            }
            continueRender(handle);
        };
        animation.addEventListener('DOMLoaded', onComplete);
        (_a = onAnimationLoadedRef.current) === null || _a === void 0 ? void 0 : _a.call(onAnimationLoadedRef, animation);
        return () => {
            animation.removeEventListener('DOMLoaded', onComplete);
            animation.destroy();
        };
    }, [animationData, handle]);
    useEffect(() => {
        if (animationRef.current && direction) {
            animationRef.current.setDirection(direction === 'backward' ? -1 : 1);
        }
    }, [direction]);
    useEffect(() => {
        if (animationRef.current && playbackRate) {
            animationRef.current.setSpeed(playbackRate);
        }
    }, [playbackRate]);
    useEffect(() => {
        var _a;
        if (!animationRef.current) {
            return;
        }
        const { totalFrames } = animationRef.current;
        const nextFrame = getNextFrame({
            currentFrame: frame * (playbackRate !== null && playbackRate !== void 0 ? playbackRate : 1),
            direction,
            loop,
            totalFrames,
        });
        animationRef.current.goToAndStop(nextFrame, true);
        const images = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('image');
        images.forEach((img) => {
            const currentHref = img.getAttributeNS('http://www.w3.org/1999/xlink', 'href');
            if (currentHref && currentHref === img.href.baseVal) {
                return;
            }
            const imgHandle = delayRender(`Waiting for lottie image with src="${img.href.baseVal}" to load`);
            // https://stackoverflow.com/a/46839799
            img.addEventListener('load', () => {
                continueRender(imgHandle);
            }, { once: true });
            img.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', img.href.baseVal);
        });
    }, [direction, frame, loop, playbackRate]);
    return jsx("div", { ref: containerRef, className: className, style: style });
};

export { Lottie, getLottieMetadata };
